import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { useLocation } from 'react-router-dom';
import {
  getProfile,
  createLesson,
  getLessons,
  deleteLesson,
  updateLesson,
  getProgrammingLessons,
  deleteProgrammingLesson,
  deleteInteractiveLesson,
  getClasses,
  createClass,
  rotateClassCode,
  getClassStudents,
  kickStudentFromClass,
  getTeacherStudents,
  createAssignment,
  getAssignments,
  getMyAssignments,
  getMyClasses,
  getClassProgress,
  getResourceAssignments,
  getMyProgress,
  getInteractiveLessons
} from '../../services/api';
import './Dashboard.css';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import LessonList from '../../components/LessonList/LessonList';
import Sidebar from '../../components/Sidebar/Sidebar';
import AssignmentModal from '../../components/AssignmentModal/AssignmentModal';
import DarkModeToggle from '../../components/DarkModeToggle/DarkModeToggle';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { handleRequest, loading } = useApi();
  const [userData, setUserData] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const role = userData?.role || user?.role;
  const roleLabelMap = {
    student: 'elev',
    teacher: 'lærer',
    admin: 'admin',
    superadmin: 'superadmin'
  };
  const levelLabelMap = {
    basic: 'Grunnleggende',
    intermediate: 'Middels',
    advanced: 'Avansert'
  };
  
  // Helper function to get assignment type label
  const getAssignmentTypeLabel = (resourceType) => {
    switch (resourceType) {
      case 'Lesson':
        return 'Quiz';
      case 'ProgrammingLesson':
        return 'Programmering';
      case 'InteractiveLesson':
        return 'Leksjon';
      default:
        return 'Tildeling';
    }
  };

  const formatVisibilityLabel = (visibleFrom) => {
    if (!visibleFrom) return null;
    const parsed = new Date(visibleFrom);
    if (Number.isNaN(parsed.getTime())) return null;
    return `Synlig fra ${parsed.toLocaleString('no-NO')}`;
  };
  
  const [lessonData, setLessonData] = useState({
    title: '',
    content: '',
    questionCount: 1,
    lessonLanguage: 'en',
    quizTypes: {
      'multiple-choice': false,
      'true-false': false,
      'short-answer': false,
      'essay': false
    }
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [lessons, setLessons] = useState([]);
  const [programmingLessons, setProgrammingLessons] = useState([]);
  const [interactiveLessons, setInteractiveLessons] = useState([]);
  const [programmingLessonSearch, setProgrammingLessonSearch] = useState('');
  const [lessonSearch, setLessonSearch] = useState('');
  const [interactiveLessonSearch, setInteractiveLessonSearch] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [creatingLesson, setCreatingLesson] = useState(false);

  // Class / roster management (teacher)
  const [classes, setClasses] = useState([]);
  const [newClassName, setNewClassName] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [classStudents, setClassStudents] = useState([]);
  const [teacherStudents, setTeacherStudents] = useState([]);

  // Assignments
  const [assignments, setAssignments] = useState([]); // teacher
  const [myAssignments, setMyAssignments] = useState([]); // student
  const [myClasses, setMyClasses] = useState([]); // student
  const [myProgress, setMyProgress] = useState(null); // student
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // 'all', 'completed', 'not-completed'
  const [progressClassId, setProgressClassId] = useState('');
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assignmentResourceType, setAssignmentResourceType] = useState(null);
  const [assignmentResourceId, setAssignmentResourceId] = useState(null);
  const [lessonAssignments, setLessonAssignments] = useState({});
  const [programmingLessonAssignments, setProgrammingLessonAssignments] = useState({});
  const [interactiveLessonAssignments, setInteractiveLessonAssignments] = useState({});
  const [classProgress, setClassProgress] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState({
    resourceType: 'Lesson',
    resourceId: '',
    classIds: [],
    studentIds: [],
    message: ''
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Fetch all data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const userResponse = await handleRequest(getProfile);
        setUserData(userResponse.data);

        if (userResponse.data?.role === 'teacher' || userResponse.data?.role === 'admin' || userResponse.data?.role === 'superadmin') {
          const [
            lessonsResponse,
            programmingLessonsResponse,
            interactiveLessonsResponse,
            classesResponse,
            studentsResponse,
            assignmentsResponse
          ] = await Promise.all([
            handleRequest(getLessons),
            handleRequest(getProgrammingLessons).catch(() => ({ data: [] })),
            handleRequest(getInteractiveLessons).catch(() => ({ data: [] })),
            handleRequest(getClasses).catch(() => ({ data: [] })),
            handleRequest(getTeacherStudents).catch(() => ({ data: [] })),
            handleRequest(getAssignments).catch(() => ({ data: [] }))
          ]);
          setLessons(lessonsResponse.data);
          setProgrammingLessons(programmingLessonsResponse.data || []);
          setInteractiveLessons(interactiveLessonsResponse.data || []);
          setClasses(classesResponse.data || []);
          setTeacherStudents(studentsResponse.data || []);
          setAssignments(assignmentsResponse.data || []);
          
          // Load assignments for each lesson and programming lesson
          const lessonAssignmentsMap = {};
          const programmingLessonAssignmentsMap = {};
          const interactiveLessonAssignmentsMap = {};
          
          for (const lesson of lessonsResponse.data) {
            try {
              const assignmentsRes = await handleRequest(getResourceAssignments, 'Lesson', lesson._id || lesson.id).catch(() => ({ data: [] }));
              lessonAssignmentsMap[lesson._id || lesson.id] = assignmentsRes.data || [];
            } catch (err) {
              // Ignore errors
            }
          }
          
          for (const lesson of programmingLessonsResponse.data || []) {
            try {
              const assignmentsRes = await handleRequest(getResourceAssignments, 'ProgrammingLesson', lesson._id || lesson.id).catch(() => ({ data: [] }));
              programmingLessonAssignmentsMap[lesson._id || lesson.id] = assignmentsRes.data || [];
            } catch (err) {
              // Ignore errors
            }
          }

          for (const lesson of interactiveLessonsResponse.data || []) {
            try {
              const assignmentsRes = await handleRequest(getResourceAssignments, 'InteractiveLesson', lesson._id || lesson.id).catch(() => ({ data: [] }));
              interactiveLessonAssignmentsMap[lesson._id || lesson.id] = assignmentsRes.data || [];
            } catch (err) {
              // Ignore errors
            }
          }
          
          setLessonAssignments(lessonAssignmentsMap);
          setProgrammingLessonAssignments(programmingLessonAssignmentsMap);
          setInteractiveLessonAssignments(interactiveLessonAssignmentsMap);
        } else {
          const [myClassesResponse, myAssignmentsResponse, myProgressResponse] = await Promise.all([
            handleRequest(getMyClasses).catch(() => ({ data: [] })),
            handleRequest(getMyAssignments).catch(() => ({ data: [] })),
            handleRequest(getMyProgress).catch(() => ({ data: null }))
          ]);
          setMyClasses(myClassesResponse.data || []);
          setMyAssignments(myAssignmentsResponse.data || []);
          setMyProgress(myProgressResponse.data);
        }
      } catch (error) {
        // Error is handled by useApi hook
      }
    };

    fetchData();
  }, [handleRequest]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (!isSidebarOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isSidebarOpen]);

  // Refresh student progress when navigating back to dashboard (e.g., after completing quiz)
  useEffect(() => {
    if (role === 'student' && location.pathname === '/dashboard') {
      const refreshProgress = async () => {
        try {
          const myProgressResponse = await handleRequest(getMyProgress).catch(() => ({ data: null }));
          setMyProgress(myProgressResponse.data);
        } catch (error) {
          // Silently fail
        }
      };
      refreshProgress();
    }
  }, [location.pathname, role, handleRequest]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Error is handled by useApi hook
    }
  };

  const handleLessonChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setLessonData(prev => ({
        ...prev,
        quizTypes: {
          ...prev.quizTypes,
          [name]: checked
        }
      }));
    } else {
      setLessonData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEdit = (lesson) => {
    const lessonId = lesson._id || lesson.id;
    navigate(`/lessons/${lessonId}/edit`);
  };

  const handleEditProgrammingLesson = (lesson) => {
    const lessonId = lesson._id || lesson.id;
    navigate(`/programming-lessons/${lessonId}/edit`);
  };

  const handleDelete = async (lessonId) => {
    if (window.confirm('Er du sikker på at du vil slette denne leksjonen?')) {
      try {
        await handleRequest(deleteLesson, lessonId);
        const lessonsResponse = await handleRequest(getLessons);
        setLessons(lessonsResponse.data);
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke slette leksjonen');
      }
    }
  };

  const handleDeleteProgrammingLesson = async (lessonId) => {
    if (window.confirm('Er du sikker på at du vil slette denne programmeringsleksjonen?')) {
      try {
        await handleRequest(deleteProgrammingLesson, lessonId);
        const programmingLessonsResponse = await handleRequest(getProgrammingLessons);
        setProgrammingLessons(programmingLessonsResponse.data);
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke slette programmeringsleksjonen');
      }
    }
  };

  const handleDeleteInteractiveLesson = async (lessonId) => {
    if (window.confirm('Er du sikker på at du vil slette denne interaktive leksjonen?')) {
      try {
        await handleRequest(deleteInteractiveLesson, lessonId);
        const interactiveLessonsResponse = await handleRequest(getInteractiveLessons);
        setInteractiveLessons(interactiveLessonsResponse.data || []);
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke slette interaktiv leksjon');
      }
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    try {
      if (!newClassName.trim()) {
        setErrorMessage('Skriv inn et klassenavn');
        return;
      }
      const response = await handleRequest(createClass, { name: newClassName });
      setClasses((prev) => [response.data, ...prev]);
      setNewClassName('');
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke opprette klasse');
    }
  };

  const handleLoadClassStudents = async (classId) => {
    try {
      setSelectedClassId(classId);
      if (!classId) {
        setClassStudents([]);
        return;
      }
      const response = await handleRequest(getClassStudents, classId);
      setClassStudents(response.data || []);
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke laste inn elever i klassen');
    }
  };

  const handleKickStudent = async (studentId) => {
    if (!selectedClassId) return;
    if (!window.confirm('Fjern denne eleven fra klassen?')) return;
    try {
      await handleRequest(kickStudentFromClass, selectedClassId, studentId);
      setClassStudents((prev) => prev.filter((s) => s._id !== studentId));
      setTeacherStudents((prev) => prev.map((s) => (s._id === studentId ? { ...s, classIds: (s.classIds || []).filter((id) => String(id) !== String(selectedClassId)) } : s)));
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke fjerne elev');
    }
  };

  const handleRotateCode = async (classId) => {
    if (!window.confirm('Rotere klassekoden? Den gamle koden vil slutte å fungere.')) return;
    try {
      const response = await handleRequest(rotateClassCode, classId);
      setClasses((prev) => prev.map((c) => (c._id === classId ? response.data : c)));
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke rotere kode');
    }
  };

  const toggleIdInArray = (arr, id) => {
    const set = new Set(arr.map(String));
    if (set.has(String(id))) set.delete(String(id));
    else set.add(String(id));
    return Array.from(set);
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    try {
      if (!assignmentForm.resourceId) {
        setErrorMessage('Velg en leksjon');
        return;
      }
      if ((assignmentForm.classIds || []).length === 0 && (assignmentForm.studentIds || []).length === 0) {
        setErrorMessage('Velg minst én klasse eller elev');
        return;
      }
      const response = await handleRequest(createAssignment, {
        resourceType: assignmentForm.resourceType,
        resourceId: assignmentForm.resourceId,
        classIds: assignmentForm.classIds,
        studentIds: assignmentForm.studentIds,
        message: assignmentForm.message
      });
      setAssignments((prev) => [response.data, ...prev]);
      setAssignmentForm({
        resourceType: assignmentForm.resourceType,
        resourceId: '',
        classIds: [],
        studentIds: [],
        message: ''
      });
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke opprette tildeling');
    }
  };

  const handleLoadProgress = async (classId) => {
    try {
      setProgressClassId(classId);
      if (!classId) {
        setClassProgress(null);
        return;
      }
      const response = await handleRequest(getClassProgress, classId);
      setClassProgress(response.data);
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke laste inn fremdrift');
    }
  };

  const handleOpenAssignmentModal = (resourceType, resourceId) => {
    setAssignmentResourceType(resourceType);
    setAssignmentResourceId(resourceId);
    setAssignmentModalOpen(true);
  };

  const handleAssignmentCreated = async () => {
    // Reload assignments for the specific resource
    if (assignmentResourceType && assignmentResourceId) {
      try {
        const assignmentsRes = await handleRequest(
          getResourceAssignments,
          assignmentResourceType,
          assignmentResourceId
        );
        
        if (assignmentResourceType === 'Lesson') {
          setLessonAssignments(prev => ({
            ...prev,
            [assignmentResourceId]: assignmentsRes.data || []
          }));
        } else {
          setProgrammingLessonAssignments(prev => ({
            ...prev,
            [assignmentResourceId]: assignmentsRes.data || []
          }));
        }
      } catch (err) {
        // Ignore errors
      }
    }
  };

  const handleSubmitLesson = async (e) => {
    e.preventDefault();
    const selectedQuizTypes = Object.entries(lessonData.quizTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([type]) => type);

    if (selectedQuizTypes.length === 0) {
      setErrorMessage('Velg minst én quiztype');
      return;
    }

    try {
      setCreatingLesson(true);
      setErrorMessage('');

      const formattedData = {
        title: lessonData.title,
        content: lessonData.content,
        questionCount: parseInt(lessonData.questionCount),
        quizTypes: selectedQuizTypes,
        lessonLanguage: lessonData.lessonLanguage || 'en'
      };

      if (isEditing) {
        const updatedLesson = await handleRequest(updateLesson, editingId, formattedData);
        setLessons(prev => prev.map(lesson => 
          lesson._id === editingId ? updatedLesson.data : lesson
        ));
      } else {
        const newLesson = await handleRequest(createLesson, formattedData);
        setLessons(prev => [...prev, newLesson.data]);
      }

      // Reset form
      setLessonData({
        title: '',
        content: '',
        questionCount: 1,
        lessonLanguage: 'en',
        quizTypes: {
          'multiple-choice': false,
          'true-false': false,
          'short-answer': false,
          'essay': false
        }
      });
      setIsEditing(false);
      setEditingId(null);
      setErrorMessage('');

    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke lagre leksjonen');
    } finally {
      setCreatingLesson(false);
    }
  };

  const hasSidebar = role === 'teacher' || role === 'admin' || role === 'superadmin';

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            {hasSidebar && (
              <button
                type="button"
                className="sidebar-toggle"
                aria-label="Åpne/lukk sidemeny"
                aria-expanded={isSidebarOpen}
                onClick={() => setIsSidebarOpen((v) => !v)}
              >
                ☰
              </button>
            )}
            <h1>Velkommen, {userData?.name || user?.name || 'bruker'}</h1>
          </div>
          <div className="header-actions">
            {(role === 'teacher' || role === 'admin' || role === 'superadmin') && (
              <>
                <div className="quick-actions">
                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate('/quiz-lessons/create')}
                    title="Opprett quizleksjon"
                  >
                    <span className="quick-action-label">Quiz</span>
                  </button>
                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate('/programming-lessons/create')}
                    title="Opprett programmeringsleksjon"
                  >
                    <span className="quick-action-label">Programmering</span>
                  </button>
                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate('/interactive-lessons/create')}
                    title="Opprett interaktiv leksjon"
                  >
                    <span className="quick-action-label">Interaktiv</span>
                  </button>
                  <button 
                    className="quick-action-btn classes-btn"
                    onClick={() => navigate('/classes')}
                    title="Se klasser og fremdrift"
                  >
                    <span className="quick-action-label">Klasser</span>
                  </button>
                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate('/competence-aims')}
                    title="Fag og kompetansemål"
                  >
                    <span className="quick-action-label">Kompetansemål</span>
                  </button>
                </div>
              </>
            )}
            {(role === 'admin' || role === 'superadmin') && (
              <button 
                className="admin-button" 
                onClick={() => navigate('/admin')}
              >
                {role === 'superadmin' ? 'Superadminpanel' : 'Adminpanel'}
              </button>
            )}
            <DarkModeToggle />
            <button 
              className="logout-button" 
              onClick={handleLogout} 
              disabled={loading}
            >
              {loading ? 'Laster...' : 'Logg ut'}
            </button>
          </div>
        </div>
      </header>
      <div className="dashboard-layout">
        {hasSidebar && (
          <>
            <div
              className={`sidebar-backdrop ${isSidebarOpen ? 'open' : ''}`}
              onClick={() => setIsSidebarOpen(false)}
            />
            <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
              <Sidebar
                role={role}
                onClassSelect={setSelectedClassId}
                selectedClassId={selectedClassId}
                onRequestClose={() => setIsSidebarOpen(false)}
              />
            </aside>
          </>
        )}
        <main className="dashboard-content">
        <div className="dashboard-card">
          <h2>Profil</h2>
          <div className="profile-info">
            <p><strong>Navn:</strong> {userData?.name || user?.name}</p>
            <p><strong>E-post:</strong> {userData?.email || user?.email}</p>
            <p><strong>Rolle:</strong> {roleLabelMap[role] || role || 'lærer'}</p>
          </div>
        </div>

        {errorMessage && (
          <ErrorCard 
            message={errorMessage} 
            onClose={() => setErrorMessage('')}
          />
        )}

        {role !== 'teacher' && role !== 'admin' && role !== 'superadmin' ? (
          <>
            <div className="dashboard-card">
              <h2>Mine klasser</h2>
              {myClasses.length > 0 ? (
                <ul className="simple-list">
                  {myClasses.map((c) => (
                    <li key={c._id}>
                      <strong>{c.name}</strong> (Lærer: {c.teacher?.name || 'Lærer'})
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-lessons">Du er ikke med i noen klasser ennå.</p>
              )}
            </div>

            <div className="dashboard-card">
              <div className="programming-lessons-header">
                <h2>Mine tildelinger</h2>
                {myAssignments.length > 0 && (
                  <div className="programming-lessons-header-actions">
                    <select
                      className="assignment-filter-select"
                      value={assignmentFilter}
                      onChange={(e) => setAssignmentFilter(e.target.value)}
                    >
                      <option value="all">Alle</option>
                      <option value="completed">Fullført</option>
                      <option value="not-completed">Ikke fullført</option>
                    </select>
                  </div>
                )}
              </div>
              {myAssignments.length > 0 ? (
                <div className="programming-lessons-list">
                  {myAssignments
                    .map((a) => {
                      const resource = a.resourceId;
                      // Extract ID properly - handle both populated object and raw ID string
                      let resourceId;
                      let title = 'Tildeling';
                      
                      if (resource && typeof resource === 'object') {
                        // If resource is populated, it has _id
                        resourceId = String(resource._id || resource.id || '');
                        title = resource.title || 'Tildeling';
                      } else if (typeof a.resourceId === 'string') {
                        resourceId = a.resourceId;
                      } else if (a.resourceId && typeof a.resourceId === 'object') {
                        resourceId = String(a.resourceId._id || a.resourceId.id || '');
                        title = a.resourceId.title || 'Tildeling';
                      } else {
                        resourceId = String(a.resourceId || '');
                      }
                      
                      // Check if assignment is completed using myProgress data
                      let isCompleted = false;
                      if (myProgress && myProgress.completedLessonIds) {
                        if (a.resourceType === 'Lesson' && myProgress.completedLessonIds.quiz) {
                          isCompleted = myProgress.completedLessonIds.quiz.includes(resourceId);
                        } else if (a.resourceType === 'ProgrammingLesson' && myProgress.completedLessonIds.programming) {
                          isCompleted = myProgress.completedLessonIds.programming.includes(resourceId);
                        } else if (a.resourceType === 'InteractiveLesson' && myProgress.completedLessonIds.interactive) {
                          isCompleted = myProgress.completedLessonIds.interactive.includes(resourceId);
                        }
                      }
                      
                      return {
                        ...a,
                        resourceId,
                        title,
                        isCompleted
                      };
                    })
                    .filter((a) => {
                      // Apply filter
                      if (assignmentFilter === 'completed') {
                        return a.isCompleted;
                      } else if (assignmentFilter === 'not-completed') {
                        return !a.isCompleted;
                      }
                      return true; // 'all'
                    })
                    .map((a) => {
                      const link =
                        a.resourceType === 'Lesson'
                          ? `/quiz/lessons/${a.resourceId}`
                          : a.resourceType === 'ProgrammingLesson'
                          ? `/programming-lessons/${a.resourceId}`
                          : `/interactive-lessons/${a.resourceId}`;
                      return (
                        <div key={a._id} className={`programming-lesson-item ${a.isCompleted ? 'assignment-completed' : ''}`}>
                          <div className="lesson-item-content">
                            <h3>
                              {a.title}
                              {a.isCompleted && (
                                <span className="completed-badge" title="Fullført">✓ Fullført</span>
                              )}
                            </h3>
                            {a.message && <p className="lesson-description">{a.message}</p>}
                            <div className="lesson-meta">
                              <span className="language-badge" style={{ marginRight: '0.5rem' }}>
                                {getAssignmentTypeLabel(a.resourceType)}
                              </span>
                              {Array.isArray(a.classIds) && a.classIds.length > 0 && (
                                <span className="task-count">Class: {a.classIds.map((c) => c.name).join(', ')}</span>
                              )}
                            </div>
                            <div className="lesson-actions">
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="lesson-link"
                              >
                                Åpne
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="no-lessons">Ingen tildelinger ennå.</p>
              )}
            </div>

            {myProgress && (
              <div className="dashboard-card">
                <h2>Min fremdrift</h2>
                <div className="progress-overview">
                  <div className="progress-section">
                    <h3>Tildelinger</h3>
                    <div className="progress-stats">
                      <div className="progress-stat">
                        <span className="stat-label">Fullført:</span>
                        <span className="stat-value">
                          {myProgress.assignments.completedAssignedCount} / {myProgress.assignments.assignedCount}
                        </span>
                      </div>
                      {myProgress.assignments.assignedCount > 0 && (
                        <div className="progress-bar">
                          <div
                            className="progress-bar-fill"
                            style={{
                              width: `${(myProgress.assignments.completedAssignedCount / myProgress.assignments.assignedCount) * 100}%`
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="progress-section">
                    <h3>Quizleksjoner</h3>
                    <div className="progress-stats">
                      <div className="progress-stat">
                        <span className="stat-label">Fullført:</span>
                        <span className="stat-value">
                          {myProgress.quiz.completedLessons} / {myProgress.quiz.totalLessons}
                        </span>
                      </div>
                      <div className="progress-stat">
                        <span className="stat-label">Antall forsøk:</span>
                        <span className="stat-value">{myProgress.quiz.totalAttempts}</span>
                      </div>
                      <div className="progress-stat">
                        <span className="stat-label">Gjennomsnittlig resultat:</span>
                        <span className="stat-value">{myProgress.quiz.averageScore}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="progress-section">
                    <h3>Programmeringsleksjoner</h3>
                    <div className="progress-stats">
                      <div className="progress-stat">
                        <span className="stat-label">Fullført:</span>
                        <span className="stat-value">
                          {myProgress.programming.completedLessons} / {myProgress.programming.totalLessons}
                        </span>
                      </div>
                      <div className="progress-stat">
                        <span className="stat-label">Fullførte oppgaver:</span>
                        <span className="stat-value">{myProgress.programming.completedTasks}</span>
                      </div>
                      <div className="progress-stat">
                        <span className="stat-label">Antall innleveringer:</span>
                        <span className="stat-value">{myProgress.programming.totalSubmissions}</span>
                      </div>
                    </div>
                  </div>

                  {myProgress.recentQuizResults && myProgress.recentQuizResults.length > 0 && (
                    <div className="progress-section">
                      <h3>Siste quizresultater</h3>
                      <div className="recent-results">
                        {myProgress.recentQuizResults.map((result, idx) => (
                          <div key={idx} className="result-item">
                            <div className="result-title">{result.lessonTitle}</div>
                            <div className="result-details">
                              Resultat: {result.score}% ({result.correctCount}/{result.totalQuestions} riktige)
                            </div>
                            <div className="result-date">
                              {new Date(result.completedAt).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {myProgress.recentProgrammingSubmissions && myProgress.recentProgrammingSubmissions.length > 0 && (
                    <div className="progress-section">
                      <h3>Siste programmeringsinnleveringer</h3>
                      <div className="recent-results">
                        {myProgress.recentProgrammingSubmissions.map((submission, idx) => (
                          <div key={idx} className="result-item">
                            <div className="result-title">
                              {submission.lessonTitle} – Oppgave {submission.taskNumber}
                            </div>
                            <div className="result-details">
                              {submission.completed ? '✓ Fullført' : '○ Pågår'} 
                              {submission.attempts > 0 && ` (${submission.attempts} forsøk)`}
                            </div>
                            <div className="result-date">
                              {new Date(submission.submittedAt).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* <div className="dashboard-card lesson-card">
              <h2>{isEditing ? 'Rediger leksjon' : 'Opprett ny leksjon'}</h2>
              <form onSubmit={handleSubmitLesson}>
                <div className="form-group">
                  <label htmlFor="title">Leksjonstittel</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={lessonData.title}
                    onChange={handleLessonChange}
                    placeholder="Skriv inn leksjonstittel"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="content">Leksjonsinnhold</label>
                  <textarea
                    id="content"
                    name="content"
                    value={lessonData.content}
                    onChange={handleLessonChange}
                    placeholder="Skriv inn dagens leksjonsinnhold"
                    rows="6"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label htmlFor="questionCount">Antall spørsmål</label>
                    <input
                      type="number"
                      id="questionCount"
                      name="questionCount"
                      value={lessonData.questionCount}
                      onChange={handleLessonChange}
                      min="1"
                      max="20"
                      required
                    />
                  </div>

                  <div className="form-group half-width">
                    <label htmlFor="lessonLanguage">Språk</label>
                    <select
                      id="lessonLanguage"
                      name="lessonLanguage"
                      value={lessonData.lessonLanguage}
                      onChange={handleLessonChange}
                      required
                    >
                      <option value="en">Engelsk</option>
                      <option value="no">Norsk</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Quiztyper</label>
                  <div className="checkbox-group">
                    <div className="checkbox-item">
                      <input
                        type="checkbox"
                        id="multiple-choice"
                        name="multiple-choice"
                        checked={lessonData.quizTypes['multiple-choice']}
                        onChange={handleLessonChange}
                      />
                      <label htmlFor="multiple-choice">Flervalg</label>
                    </div>
                    <div className="checkbox-item">
                      <input
                        type="checkbox"
                        id="true-false"
                        name="true-false"
                        checked={lessonData.quizTypes['true-false']}
                        onChange={handleLessonChange}
                      />
                      <label htmlFor="true-false">Sant/usant</label>
                    </div>
                    <div className="checkbox-item">
                      <input
                        type="checkbox"
                        id="short-answer"
                        name="short-answer"
                        checked={lessonData.quizTypes['short-answer']}
                        onChange={handleLessonChange}
                      />
                      <label htmlFor="short-answer">Kortsvar</label>
                    </div>
                    <div className="checkbox-item">
                      <input
                        type="checkbox"
                        id="essay"
                        name="essay"
                        checked={lessonData.quizTypes['essay']}
                        onChange={handleLessonChange}
                      />
                      <label htmlFor="essay">Essay</label>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <button 
                    type="submit" 
                    className="create-lesson-btn"
                    disabled={creatingLesson}
                  >
                    {creatingLesson 
                      ? (isEditing ? 'Oppdaterer leksjon...' : 'Oppretter leksjon...') 
                      : (isEditing ? 'Oppdater leksjon' : 'Opprett leksjon')
                    }
                  </button>
                  {isEditing && (
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => {
                        setIsEditing(false);
                        setEditingId(null);
                        setLessonData({
                          title: '',
                          content: '',
                          questionCount: 1,
                          lessonLanguage: 'en',
                          quizTypes: {
                            'multiple-choice': false,
                            'true-false': false,
                            'short-answer': false,
                            'essay': false
                          }
                        });
                        setErrorMessage('');
                      }}
                    >
                      Avbryt
                    </button>
                  )}
                </div>
              </form>
            </div> */}

       

            {/* <div className="dashboard-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2>Elevfremdrift</h2>
                {progressClassId && (
                  <button
                    className="view-full-progress-btn"
                    onClick={() => navigate(`/classes/${progressClassId}/progress`)}
                  >
                    Vis full fremdrift
                  </button>
                )}
              </div>
              {classes.length > 0 ? (
                <>
                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#333' }}>Klasse</label>
                    <select 
                      value={progressClassId} 
                      onChange={(e) => handleLoadProgress(e.target.value)}
                      style={{ 
                        width: '100%', 
                        maxWidth: '400px',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Velg…</option>
                      {classes.map((c) => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {classProgress?.students?.length ? (
                    <div className="results-table-container">
                      <table className="results-table">
                        <thead>
                          <tr>
                            <th>Elev</th>
                            <th>Quizforsøk</th>
                            <th>Snitt quizscore</th>
                            <th>Prog. fullførte oppgaver</th>
                            <th>Prog. innleveringer</th>
                            <th>Tildelte fullført</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classProgress.students.map((row) => (
                            <tr key={row.student._id}>
                              <td>{row.student.name} ({row.student.email})</td>
                              <td>{row.quiz.attempts}</td>
                              <td>{row.quiz.avgScore}%</td>
                              <td>{row.programming.completedTasks}</td>
                              <td>{row.programming.submissions}</td>
                              <td>{row.assignments.completedAssignedCount}/{row.assignments.assignedCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : progressClassId ? (
                    <p className="no-lessons">Ingen fremdrift registrert ennå for denne klassen.</p>
                  ) : null}
                </>
              ) : (
                <p className="no-lessons">Opprett en klasse for å begynne å følge med på fremdrift.</p>
              )}
            </div> */}

            {/* <div className="dashboard-card">
              <h2>Assign Lessons / Quizzes</h2>
              <form onSubmit={handleCreateAssignment}>
                <div className="form-row">
                  <div className="form-group half-width">
                    <label>Resource Type</label>
                    <select
                      value={assignmentForm.resourceType}
                      onChange={(e) => setAssignmentForm((prev) => ({ ...prev, resourceType: e.target.value, resourceId: '' }))}
                    >
                      <option value="Lesson">Quiz Lesson</option>
                      <option value="ProgrammingLesson">Programming Lesson</option>
                    </select>
                  </div>
                  <div className="form-group half-width">
                    <label>Lesson</label>
                    <select
                      value={assignmentForm.resourceId}
                      onChange={(e) => setAssignmentForm((prev) => ({ ...prev, resourceId: e.target.value }))}
                    >
                      <option value="">Select…</option>
                      {(assignmentForm.resourceType === 'Lesson' 
                        ? lessons 
                        : assignmentForm.resourceType === 'ProgrammingLesson' 
                        ? programmingLessons 
                        : interactiveLessons).map((l) => (
                        <option key={l._id || l.id} value={l._id || l.id}>{l.title}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Message (optional)</label>
                  <input
                    type="text"
                    value={assignmentForm.message}
                    onChange={(e) => setAssignmentForm((prev) => ({ ...prev, message: e.target.value }))}
                    placeholder="e.g. Due Friday"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label>Assign to Classes</label>
                    <div className="checkbox-group">
                      {classes.map((c) => (
                        <div key={c._id} className="checkbox-item">
                          <input
                            type="checkbox"
                            id={`assign-class-${c._id}`}
                            checked={(assignmentForm.classIds || []).map(String).includes(String(c._id))}
                            onChange={() => setAssignmentForm((prev) => ({ ...prev, classIds: toggleIdInArray(prev.classIds || [], c._id) }))}
                          />
                          <label htmlFor={`assign-class-${c._id}`}>{c.name}</label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group half-width">
                    <label>Assign to Students</label>
                    <div className="checkbox-group">
                      {teacherStudents.map((s) => (
                        <div key={s._id} className="checkbox-item">
                          <input
                            type="checkbox"
                            id={`assign-student-${s._id}`}
                            checked={(assignmentForm.studentIds || []).map(String).includes(String(s._id))}
                            onChange={() => setAssignmentForm((prev) => ({ ...prev, studentIds: toggleIdInArray(prev.studentIds || [], s._id) }))}
                          />
                          <label htmlFor={`assign-student-${s._id}`}>{s.name} ({s.email})</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <button type="submit" className="create-lesson-btn">Assign</button>
              </form>

              <div style={{ marginTop: 16 }}>
                <h3>Recent Assignments</h3>
                {assignments.length > 0 ? (
                  <ul className="simple-list">
                    {assignments.slice(0, 10).map((a) => (
                      <li key={a._id}>
                        <strong>{a.resourceId?.title || 'Lesson'}</strong>{' '}
                        <span>
                          → {a.classIds?.length ? `Classes: ${a.classIds.map((c) => c.name).join(', ')}` : ''}
                          {a.studentIds?.length ? `${a.classIds?.length ? ' | ' : ''}Students: ${a.studentIds.length}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-lessons">No assignments created yet.</p>
                )}
              </div>
            </div> */}
            <div className="dashboard-card">
              <div className="programming-lessons-header">
                <h2>Dine interaktive leksjoner</h2>
                <div className="programming-lessons-header-actions">
                  <input
                    type="text"
                    className="programming-lesson-search"
                    placeholder="Søk etter tema..."
                    value={interactiveLessonSearch}
                    onChange={(e) => setInteractiveLessonSearch(e.target.value)}
                  />
                  <button 
                    className="create-programming-lesson-btn"
                    onClick={() => navigate('/interactive-lessons/create')}
                  >
                    + Opprett interaktiv leksjon
                  </button>
                </div>
              </div>
              {interactiveLessons.length > 0 ? (
                <div className="programming-lessons-list">
                  {interactiveLessons
                    .filter((lesson) => {
                      if (!interactiveLessonSearch.trim()) return true;
                      const searchTerm = interactiveLessonSearch.toLowerCase();
                      return (
                        lesson.title?.toLowerCase().includes(searchTerm) ||
                        lesson.topic?.toLowerCase().includes(searchTerm) ||
                        lesson.subjectArea?.toLowerCase().includes(searchTerm)
                      );
                    })
                    .map((lesson) => (
                    <div key={lesson._id || lesson.id} className="programming-lesson-item">
                      <div className="lesson-item-content">
                        <h3>{lesson.title}</h3>
                        <div className="lesson-meta">
                          <span className="language-badge">{lesson.subjectArea.toUpperCase()}</span>
                          <span className="task-count">{lesson.screensLength} slides</span>
                          <span className="task-count">{lesson.durationMinutes} min</span>
                        </div>
                        {lesson.topic && (
                          <p className="lesson-description">Tema: {lesson.topic}</p>
                        )}
                        {interactiveLessonAssignments[lesson._id || lesson.id]?.length > 0 && (
                          <div className="assignment-info">
                            <strong>Tildelt til:</strong>
                            <div className="assignment-details">
                              {interactiveLessonAssignments[lesson._id || lesson.id].map((assignment, idx) => {
                                const visibilityLabel = formatVisibilityLabel(assignment.visibleFrom);
                                return (
                                  <div key={idx} className="assignment-item">
                                    {assignment.classIds?.length > 0 && (
                                      <span className="assignment-classes">
                                        Klasser: {assignment.classIds.map(c => c.name).join(', ')}
                                      </span>
                                    )}
                                    {assignment.studentIds?.length > 0 && (
                                      <span className="assignment-students">
                                        Elever: {assignment.studentIds.map(s => s.name).join(', ')}
                                      </span>
                                    )}
                                    {visibilityLabel && (
                                      <span className="assignment-visibility">
                                        {visibilityLabel}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="lesson-actions">
                          <button
                            className="assign-btn"
                            onClick={() => handleOpenAssignmentModal('InteractiveLesson', lesson._id || lesson.id)}
                          >
                            Tildel
                          </button>
                          <button
                            className="edit-btn"
                            onClick={() => navigate(`/interactive-lessons/${lesson._id || lesson.id}/edit`)}
                          >
                            Rediger
                          </button>
                          <a 
                            href={`/interactive-lessons/${lesson._id || lesson.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lesson-link"
                          >
                            Vis leksjon
                          </a>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteInteractiveLesson(lesson._id || lesson.id)}
                          >
                            Slett
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="programming-lessons-empty">
                  <h3>Ingen interaktive leksjoner ennå</h3>
                  <p>Opprett din første interaktive leksjon med AI-genererte slides som holder elevene engasjert med hyppige mikrointeraksjoner.</p>
                  <button 
                    className="create-programming-lesson-btn empty-state-btn"
                    onClick={() => navigate('/interactive-lessons/create')}
                  >
                    Opprett interaktiv leksjon
                  </button>
                </div>
              )}
            </div>
            <div className="dashboard-card">
              <div className="programming-lessons-header">
                <h2>Dine quizleksjoner</h2>
                <div className="programming-lessons-header-actions">
                  <input
                    type="text"
                    className="programming-lesson-search"
                    placeholder="Søk etter tema..."
                    value={lessonSearch}
                    onChange={(e) => setLessonSearch(e.target.value)}
                  />
                </div>
              </div>
              {lessons.length > 0 ? (
                <LessonList 
                  lessons={lessons.filter((lesson) => {
                    if (!lessonSearch.trim()) return true;
                    const searchTerm = lessonSearch.toLowerCase();
                    return (
                      lesson.title?.toLowerCase().includes(searchTerm) ||
                      lesson.content?.toLowerCase().includes(searchTerm)
                    );
                  })}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onAssign={(lessonId) => handleOpenAssignmentModal('Lesson', lessonId)}
                  assignments={lessonAssignments}
                  baseUrl={window.location.origin}
                />
              ) : (
                <p className="no-lessons">Ingen quizleksjoner opprettet ennå.</p>
              )}
            </div>

            <div className="dashboard-card">
              <div className="programming-lessons-header">
                <h2>Dine programmeringsleksjoner</h2>
                <div className="programming-lessons-header-actions">
                  <input
                    type="text"
                    className="programming-lesson-search"
                    placeholder="Søk etter tema..."
                    value={programmingLessonSearch}
                    onChange={(e) => setProgrammingLessonSearch(e.target.value)}
                  />
                  <button 
                    className="create-programming-lesson-btn"
                    onClick={() => navigate('/programming-lessons/create')}
                  >
                    + Opprett programmeringsleksjon
                  </button>
                </div>
              </div>
              {programmingLessons.length > 0 ? (
                <div className="programming-lessons-list">
                  {programmingLessons
                    .filter((lesson) => {
                      if (!programmingLessonSearch.trim()) return true;
                      const searchTerm = programmingLessonSearch.toLowerCase();
                      return (
                        lesson.title?.toLowerCase().includes(searchTerm) ||
                        lesson.description?.toLowerCase().includes(searchTerm) ||
                        lesson.language?.toLowerCase().includes(searchTerm) ||
                        lesson.level?.toLowerCase().includes(searchTerm)
                      );
                    })
                    .map((lesson) => (
                    <div key={lesson._id || lesson.id} className="programming-lesson-item">
                      <div className="lesson-item-content">
                        <h3>{lesson.title}</h3>
                        <div className="lesson-meta">
                          <span className="language-badge">{lesson.language.toUpperCase()}</span>
                          {lesson.level && (
                            <span className={`level-badge level-${lesson.level}`}>
                              {levelLabelMap[lesson.level] || (lesson.level.charAt(0).toUpperCase() + lesson.level.slice(1))}
                            </span>
                          )}
                          <span className="task-count">{lesson.tasks?.length || 0} {lesson.tasks?.length === 1 ? 'oppgave' : 'oppgaver'}</span>
                        </div>
                        {lesson.description && (
                          <p className="lesson-description">{lesson.description}</p>
                        )}
                        {programmingLessonAssignments[lesson._id || lesson.id]?.length > 0 && (
                          <div className="assignment-info">
                            <strong>Tildelt til:</strong>
                            <div className="assignment-details">
                              {programmingLessonAssignments[lesson._id || lesson.id].map((assignment, idx) => {
                                const visibilityLabel = formatVisibilityLabel(assignment.visibleFrom);
                                return (
                                  <div key={idx} className="assignment-item">
                                    {assignment.classIds?.length > 0 && (
                                      <span className="assignment-classes">
                                        Klasser: {assignment.classIds.map(c => c.name).join(', ')}
                                      </span>
                                    )}
                                    {assignment.studentIds?.length > 0 && (
                                      <span className="assignment-students">
                                        Elever: {assignment.studentIds.map(s => s.name).join(', ')}
                                      </span>
                                    )}
                                    {visibilityLabel && (
                                      <span className="assignment-visibility">
                                        {visibilityLabel}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="lesson-actions">
                          <button
                            className="assign-btn"
                            onClick={() => handleOpenAssignmentModal('ProgrammingLesson', lesson._id || lesson.id)}
                          >
                            Tildel
                          </button>
                          <button
                            className="view-results-btn"
                            onClick={() => navigate(`/programming-lessons/${lesson._id || lesson.id}/results`)}
                          >
                            Vis resultater
                          </button>
                          <button
                            className="edit-btn"
                            onClick={() => handleEditProgrammingLesson(lesson)}
                          >
                            Rediger
                          </button>
                          <a 
                            href={`/programming-lessons/${lesson._id || lesson.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lesson-link"
                          >
                            Vis leksjon
                          </a>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteProgrammingLesson(lesson._id || lesson.id)}
                          >
                            Slett
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="programming-lessons-empty">
                  <h3>Ingen programmeringsleksjoner ennå</h3>
                  <p>Opprett din første programmeringsleksjon for å hjelpe elevene å lære HTML, CSS og JavaScript gjennom praktiske kodeoppgaver.</p>
                  <button 
                    className="create-programming-lesson-btn empty-state-btn"
                    onClick={() => navigate('/programming-lessons/create')}
                  >
                    Opprett programmeringsleksjon
                  </button>
                </div>
              )}
            </div>

           
          </>
        )}
      </main>
      </div>
      
      {(role === 'teacher' || role === 'admin' || role === 'superadmin') && (
        <AssignmentModal
          isOpen={assignmentModalOpen}
          onClose={() => {
            setAssignmentModalOpen(false);
            setAssignmentResourceType(null);
            setAssignmentResourceId(null);
          }}
          resourceType={assignmentResourceType}
          resourceId={assignmentResourceId}
          onAssignmentCreated={handleAssignmentCreated}
        />
      )}
    </div>
  );
};

export default Dashboard; 